import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { REACH_API_COMMON_HEADERS } from '~/core/tokens';

export type ApiParams =
	| HttpParams
	| Record<string, string | number | Array<string | number> | boolean>;

@Injectable({ providedIn: 'root' })
export class ApiHttpClientService {
	protected readonly http = inject(HttpClient);
	protected readonly commonHeaders = inject(REACH_API_COMMON_HEADERS);

	public getObs<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string
	): Observable<T> {
		const options = {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			responseType: responseType as any,
		};
		return this.http.get<T>(path, options);
	}

	public async get<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string
	): Promise<T> {
		return await firstValueFrom(this.getObs<T>(path, params, headers, responseType));
	}

	public async getWithHeaders<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<HttpResponse<T>> {
		const responseType: 'json' = 'json' as const;
		const observe: 'response' = 'response' as const;

		const options = {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			responseType,
			observe,
		};
		return await firstValueFrom(this.http.get(path, options) as Observable<HttpResponse<T>>);
	}

	public async post<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string
	): Promise<T> {
		return await firstValueFrom(
			this.http.post<T>(path, body, {
				params,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				responseType: responseType as any,
			})
		);
	}

	public async patch<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.patch<T>(path, body, {
				params,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		);
	}

	public async put<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.put<T>(path, body, {
				params,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		);
	}

	public async delete<T>(
		path: string,
		body: unknown = {},
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.delete<T>(path, {
				body,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		);
	}
}
